import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Loader2, Gift, AlertCircle } from 'lucide-react';
import apiClient from '@/utils/api';
import { toast } from 'sonner';

/**
 * Invitation link landing (Phase 3 — V5.0). Route: /i/:code
 * Resolves the invitation (records a click), stashes any attached coupon in
 * localStorage for auto-apply at checkout, then redirects to the landing path.
 */
export default function InviteLanding() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data } = await apiClient.get(`/invitations/resolve/${code}`);
        if (!active) return;
        if (!data.valid) {
          setError(data.reason || 'This invitation link is not valid.');
          return;
        }
        if (data.attached_coupon) {
          localStorage.setItem(
            'zenstudios_pending_coupon',
            JSON.stringify({ code: data.attached_coupon.code, business_unit: data.attached_coupon.business_unit })
          );
          toast.success(`Coupon ${data.attached_coupon.code} unlocked — apply it at checkout!`);
        }
        localStorage.setItem('zenstudios_invite_code', data.code);
        setTimeout(() => navigate(data.landing_path || '/'), 800);
      } catch {
        if (active) setError('Could not open this invitation link.');
      }
    })();
    return () => { active = false; };
  }, [code, navigate]);

  return (
    <div className="min-h-[60vh] flex items-center justify-center px-6" data-testid="invite-landing">
      <div className="text-center max-w-md">
        {error ? (
          <>
            <AlertCircle className="mx-auto text-rose-500 mb-4" size={40} />
            <h1 className="text-2xl font-semibold mb-2">Invitation unavailable</h1>
            <p className="text-gray-500 mb-6">{error}</p>
            <button onClick={() => navigate('/')} className="px-5 py-2.5 rounded-lg bg-gray-900 text-white">Go to homepage</button>
          </>
        ) : (
          <>
            <Gift className="mx-auto text-amber-500 mb-4 animate-bounce" size={40} />
            <h1 className="text-2xl font-semibold mb-2">You've been invited!</h1>
            <p className="text-gray-500 flex items-center justify-center gap-2">
              <Loader2 size={16} className="animate-spin" /> Taking you there…
            </p>
          </>
        )}
      </div>
    </div>
  );
}
